import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useLocation, useNavigate, Link } from 'react-router-dom';

import Messages from "../../../constants/Messages";
import uRLs from "../../../utils/baseUtils";
import { useShowAlert } from "../../../context/AlertContext";
import { useAuth } from "../../../context/AuthProvider";
import { useNetwork } from "../../../context/NetworkContext";
import { ChevronLeftIcon } from "../../../constants/Icons";

const VerifyOtp = () => {
    const { handleLogin } = useAuth();
    const { checkNetwork } = useNetwork();
    const showAlert = useShowAlert();
    const navigate = useNavigate();
    const location = useLocation();

    const verifyToken = location.state?.verifyToken;
    const email = location.state?.email;
    const label = location.state?.label;
    const isfrom = location.state?.isfrom;
    const successRedirect = location.state?.successRedirect;
    const failureRedirect = location.state?.failureRedirect;

    const otpInputRef = useRef(null);
    const [isVerifyData, setVerifyData] = useState({ verifycode: '' });
    const [isBtnLoading, setBtnLoading] = useState(false);
    const [isResendBtnLoading, setResendBtnLoading] = useState(false);
    const [error, setErrors] = useState({});
    const [errorMsg, setErrorMsg] = useState(null);

    useEffect(() => {
        if (!verifyToken || !successRedirect || !failureRedirect) {
            navigate("/" + failureRedirect + "");
        }
    }, [verifyToken, navigate]);

    const validateForm = () => {
        const errors = {};
        if (isVerifyData.verifycode === undefined || isVerifyData.verifycode === null || isVerifyData.verifycode === "") {
            errors.verifycode = Messages.AUTH.OTP_REQUIRED;
        } else if (isVerifyData.length < 6) {
            errors.verifycode = Messages.AUTH.OTP_INVALID;
        }
        setErrors(errors);
        if (Object.keys(errors).length > 0) {
            const firstErrorField = Object.keys(errors)[0];
            switch (firstErrorField) {
                case 'verifycode':
                    otpInputRef.current?.focus();
                    break;
                default:
                    break;
            }
        }
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const cleanedValue = name === "verifycode" ? value.replace(/[^0-9]/g, "") : value.trim();
        const updatedData = {
            ...isVerifyData,
            [name]: cleanedValue,
        };
        setVerifyData(updatedData);
    };

    const handleVerifyOTP = () => {
        setErrorMsg(null);
        if (!validateForm()) return;
        handleAccount();
    };

    const handleAccount = async () => {
        setBtnLoading(true);
        try {
            if (!checkNetwork()) return;
            const response = await axios.post(`${uRLs.AUTH_URL}auth-otp`, isVerifyData, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${verifyToken}`,
                },
            });
            if (response.status === 201 && response.data.results.accessToken) {
                var accessToken = response.data.results.accessToken;
                if (isfrom === 'FORGOT') {
                    showAlert("success", Messages.AUTH.LOGIN_VERIFIED);
                    navigate(`/${successRedirect}`, { state: { accessToken: accessToken, isfrom: isfrom, label: 'Back to Login', successRedirect: 'login', failureRedirect: 'forgot' } });
                } else if (isfrom === 'RESET') {
                    showAlert("success", Messages.AUTH.LOGIN_VERIFIED);
                    navigate(`/${successRedirect}`, { state: { accessToken: accessToken, isfrom: isfrom, label: 'Back to Login', successRedirect: 'login', failureRedirect: 'reset' } });
                } else {
                    const respon = await axios.get(`${uRLs.API_URL}auth-store`, {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${accessToken}`,
                        },
                    });
                    if (respon.status === 200 && respon.data.results.accessToken) {
                        handleLogin(respon.data.results);
                        showAlert("success", Messages.AUTH.LOGIN_SUCCESS);
                        navigate(`/${successRedirect}`, { replace: true, state: null });
                    }
                }

            }
        } catch (error) {
            if (error.response?.data?.code === 'TOKEN_EXPIRED') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else if (error.response?.data?.code === 'FORBIDDEN') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else if (error.response?.data?.code === 'STORE_NOT_FOUND') {
                showAlert("success", Messages.AUTH.LOGIN_SUCCESS);
                navigate(`/authstore`, { state: { accessToken: accessToken, label: label, successRedirect: successRedirect, failureRedirect: failureRedirect } });
            } else {
                setErrorMsg(error.response?.data?.message || Messages.AUTH.OTP_FAILED);
            }
        } finally {
            setBtnLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setResendBtnLoading(true);
        try {
            if (!checkNetwork()) return;
            const response = await axios.get(`${uRLs.AUTH_URL}auth-resend-otp`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${verifyToken}`,
                },
            });
            showAlert("success", Messages.AUTH.OTP_RESENT);
        } catch (error) {
            if (error.response?.data?.code === 'TOKEN_EXPIRED') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else if (error.response?.data?.code === 'FORBIDDEN') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else {
                setErrorMsg(error.response?.data?.message || Messages.AUTH.OTP_FAILED);
            }
        } finally {
            setResendBtnLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleVerifyOTP();
        }
    };

    return (
        <div class="wrapper">
            <div class="authentication-reset-password d-flex align-items-center justify-content-center">
                <div class="container">
                    <div class="row row-cols-1 row-cols-lg-2 row-cols-xl-3">
                        <div class="col mx-auto">
                            <div class="card">
                                <div class="card-body">
                                    <div class="p-4">
                                        <div class="mb-4 text-center">
                                            <img src="assets/images/logo-icon.png" width="60" alt="" />
                                        </div>
                                        <div class="text-start mb-4">
                                            <h4 className="text-center mt-5 fw-400">OTP Verification</h4>
                                            <p className="text-center mb-0">One Time Password</p>
                                            <p className="text-center text-success mb-0">(OTP) has been sent successfully to</p>
                                            <p className="text-center mb-4"> {email} </p>
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label htmlFor="verifycode" className="form-label">OTP
                                                    <span className="text-danger"> *</span>
                                                </label>
                                                <label htmlFor="verifycode" className="form-label text-right">
                                                    {isResendBtnLoading ? (
                                                        <button className="btn btn-sm bg-label-send shadow-none pb-0 pt-1" disabled>
                                                            <CircularProgress size={18} className="text-white" />
                                                        </button>
                                                    ) : (
                                                        <button type="button" onClick={handleResendOTP} className="btn btn-sm bg-label-send shadow-none">
                                                            Resend OTP
                                                        </button>
                                                    )}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    autoComplete="off"
                                                    value={isVerifyData.verifycode}
                                                    onChange={handleChange}
                                                    onKeyPress={(e) => {
                                                        if (!/[0-9]/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    name="verifycode"
                                                    pattern="[0-9]*"
                                                    inputMode="numeric"
                                                    maxLength="6"
                                                    ref={otpInputRef}
                                                    onKeyDown={handleKeyDown}
                                                />
                                                {error.verifycode && <div className="invalid-feedback d-block">{error.verifycode}</div>}
                                            </div>

                                            {errorMsg && <div className="invalid-feedback">{errorMsg}</div>}

                                            <div className="col-12">
                                                <div className="d-grid">
                                                    {isBtnLoading ? (
                                                        <button className="btn btn-primary pb-0" disabled>
                                                            <CircularProgress size={22} className="text-white" />
                                                        </button>
                                                    ) : (
                                                        <button type="button" onClick={handleVerifyOTP} className="btn btn-primary">
                                                            Verify OTP
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="text-center">
                                                    <Link
                                                        to={`/${failureRedirect}`}
                                                        className="d-flex align-items-center justify-content-center text-decoration-none">
                                                        <ChevronLeftIcon iClass={"me-1 fs-18"}/>
                                                        {label}
                                                    </Link>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyOtp;